import React from 'react';
import { FieldProps } from './types';
import { Section } from '../../../layout/section';
import { FormBuilderContext } from '../formBuilderContext';
import { observer } from 'mobx-react';
import { renderProperty } from 'controls/react/form/formBuilderNew/components';
import { AntSwitch } from 'controls/react/ant/antSwitch';
import { AntSelect } from '../../../ant/ant-select/antSelect';
import { AntInputNumber } from '../../../ant/antInputNumber';
import { Toolbar, ToolbarItemPosition } from 'controls/react/layout/toolbar';
import { FormEntryNew } from '../../formEntryNew';
import { OPERATORS } from './ceAlgorithm';
import { AlgorithmTypeOption, AlgorithmValue, Property } from '../models';
import { translator } from 'core/localization/localization';
import { fieldNamesTextValue } from 'controls/react/ant/ant-select/types';

const i18n = translator({
	'Warning condition': {
		en: 'Warning condition',
		no: 'Advarsel tilstand',
	},
	'Warning condition if breached will give a WARNING state for servicequalifier': {
		en: 'Warning condition if breached will give a WARNING state for servicequalifier',
		no: 'Brudd på advarsel tilstand gir en ADVARSEL tilstand for tjenestepunktet.',
	},
	'Static threshold': { en: 'Static threshold', no: 'Statisk terskelverdi' },
	'None': { en: 'None', no: 'Ingen' },
	'Algorithm': { en: 'Algorithm', no: 'Algoritme' },
	'Operator': { en: 'Operator', no: 'Operator' },
	'Threshold': { en: 'Threshold', no: 'Terskelverdi' },
	'Unit': { en: 'Unit', no: 'Enhet' },
});

// The Service Qualifier wizard's `grpBreachThreshold` group carries a "warning" value that has no corresponding
// Property node in the configuration tree at all — it was a hardcoded UI addition in the old formBuilder's group.js,
// sharing the breach group's own CEAlgorithm field's datasource (matched by type-prefix, e.g. 'StaticThreshold'
// against 'StaticThresholdAlgorithm'). Ported as-is rather than generalized, since the backend contract itself
// is this special-cased — see qualifierWizard's `properties.warning` in the old redux-form.
const WARNING_PROPERTY_ID = 'warning';

const WarningCondition = observer(({ group }: { group: Property }) => {
	const store = React.useContext(FormBuilderContext);
	const algorithmProperty = group.properties.find((x) => x.type === 'CEAlgorithm');
	if (!algorithmProperty) return null;

	const algorithmData = (store.dataSources.get(algorithmProperty.id) as AlgorithmTypeOption[]) || [];
	// only operator/threshold/thresholdUnit are meaningful here; a non-null value means "static-threshold warning"
	const warningValue = store.value[WARNING_PROPERTY_ID] as AlgorithmValue;
	const type = warningValue ? 'StaticThreshold' : 'None';
	const currentTypeData = algorithmData.find((x) => x.value.startsWith(type));
	const unitList = currentTypeData?.unitList ?? [];

	const onTypeChange = (newType: string) => {
		if (newType === 'None') {
			store.value[WARNING_PROPERTY_ID] = null;
			return;
		}
		// mirror the breach threshold's own unit, since the warning threshold measures the same metric
		const mainValue = store.value[algorithmProperty.id] as AlgorithmValue;
		store.value[WARNING_PROPERTY_ID] = { operator: 'LE', threshold: 70, thresholdUnit: mainValue?.thresholdUnit };
	};

	return (
		<Section appearance={'frame'} childrenPadding title={i18n('Warning condition')}>
			<FormEntryNew label={i18n('Algorithm')} alwaysWithValue>
				<AntSelect
					options={[
						{ text: i18n('None'), value: 'None' },
						{ text: i18n('Static threshold'), value: 'StaticThreshold' },
					]}
					fieldNames={fieldNamesTextValue}
					value={type}
					onChange={onTypeChange}
				/>
			</FormEntryNew>
			{type === 'StaticThreshold' && (
				<>
					<FormEntryNew label={i18n('Operator')} model={warningValue} modelField={'operator'}>
						<AntSelect options={OPERATORS} fieldNames={fieldNamesTextValue} />
					</FormEntryNew>
					<FormEntryNew label={i18n('Threshold')} model={warningValue} modelField={'threshold'}>
						<AntInputNumber />
					</FormEntryNew>
					{!!unitList.length && (
						<FormEntryNew label={i18n('Unit')} model={warningValue} modelField={'thresholdUnit'}>
							<AntSelect options={unitList} fieldNames={fieldNamesTextValue} />
						</FormEntryNew>
					)}
				</>
			)}
		</Section>
	);
});

export const CEGroup = observer(({ property: group }: FieldProps) => {
	const store = React.useContext(FormBuilderContext);
	const { switchable } = group;
	const isBreachGroup = group.id === 'grpBreachThreshold';
	const isMeasuredTargetGroup = group.id === 'grpMeasuredTarget';
	return (
		<>
			<Section
				appearance={'frame'}
				childrenPadding={true}
				title={group.caption}
				direction={group.singleRow ? 'row' : 'column'}
				layout={group.singleRow ? 'equal' : 'stack'}
			>
				{switchable && (
					<Toolbar>
						<AntSwitch
							position={ToolbarItemPosition.AT_THE_END}
							size={'small'}
							onChange={(value) => store.setGroupSwitch(group.id, value)}
							value={store.getGroupSwitch(group.id)}
						/>
					</Toolbar>
				)}
				{group.properties.map((x) => renderProperty(x, store))}
			</Section>
			{isBreachGroup && <WarningCondition group={group} />}
			{isMeasuredTargetGroup && store.measuredTargetSlot}
		</>
	);
});
